import { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useCheckInTransition, CheckInTransitionOverlay } from '../components/home-v2/check-in-transition/index.js';
import { containerVariants, itemVariants } from '../lib/animations';
import {
  HomePageLayout,
  HomeV2FiltersContainer,
  FilterDialog,
  HOME_V2_COLORS,
  useTimeOfDay,
  TimeOfDayMascot,
  // DevPeriodSwitcher,
} from '../components/home-v2';
//import type { TimePeriod } from '../components/home-v2';
import {
  CardDeck,
  //  SwipeInstruction,
  ErrorMessage,
  NoActivitiesCard,
} from '../components/card-deck';
import type { DeckCardData, IntroCardData, JourneyCardData } from '../components/card-deck/types';
import { useFilter } from '../contexts';
import { useAuth } from '../contexts/AuthContext';
import ArrowRightIcon from '../components/icons/ArrowRightIcon';
import { useCardSwipeIntroModal } from '../hooks/useCardSwipeIntroModal';
import { useHomeActivities } from '../hooks/useHomeActivities';
import { useChildren } from '../hooks/useAssessment';
import { transformRecommendedActivitiesToCards } from '../utils/activityTransformer';

const SHOW_FILTER = import.meta.env.DEV ?? false

const HIDDEN_CONTENT_DOMAINS = ['sparks.playmoments.app', 'spark.playmoments.app']

export const useContentManager = () => {
  const hideContent = import.meta.env.DEV || HIDDEN_CONTENT_DOMAINS.includes(window.location.hostname)

  return {
    hideCloud: hideContent,
    hideFilterBar: hideContent,
    hideHeaderAvatar: hideContent,
    hideSparksHistory: hideContent,
    hideMoodsReport: hideContent,
  }
}

/**
 * HomeV2Page
 *
 * Main home page using the new Home V2 design.
 * Route: /home-v2
 *
 * Displays the cloud mascot character, filter bar, and activity card deck.
 * Fetches published activities from backend and displays:
 * - Skeleton card deck while fetching (3-card stacked deck with loading design)
 * - Card deck if 3+ activities available
 * - "No Activities" card if <3 activities
 * - Error message if fetch fails
 */
export default function HomeV2Page() {
  const { track } = useAnalytics();
  const { isDialogOpen, openDialog, closeDialog } = useFilter();
  const { isVisible: isIntroVisible, dismiss: dismissIntro } = useCardSwipeIntroModal();
  //const [overridePeriod, setOverridePeriod] = useState<TimePeriod | null>(null);
  //const { period, config } = useTimeOfDay({ overridePeriod });
  const { period, config } = useTimeOfDay();
  //const { config } = useTimeOfDay();
  const {
    cloudRef,
    isTransitioning,
    cloudRect,
    startTransition: startTransitionBase,
    onTransitionComplete,
    resetTransition,
  } = useCheckInTransition();

  const startTransition = useCallback(() => {
    track('check_in_start', { entry_point: 'cloud_mascot' });
    startTransitionBase();
  }, [track, startTransitionBase]);

  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  // Auto-select first child (v1 behavior — v2 will support multi-child selection via UI)
  const { data: children } = useChildren();
  const firstChild = children?.[0] ?? null;

  const {
    data: recommendationsData,
    isLoading: isLoadingRecs,
    error: recsError,
    refetch: refetchRecs,
  } = useHomeActivities(timezone);

  const { hasCompletedAssessment } = useAuth();

  // Transform recommendations to card format
  const cards = useMemo(() => {
    if (!recommendationsData?.activities) return undefined;
    return transformRecommendedActivitiesToCards(recommendationsData.activities, firstChild);
  }, [recommendationsData, firstChild]);

  const cardsWithAll = useMemo(() => {
    if (!cards) return undefined;
    let result: DeckCardData[] = cards;
    if (!hasCompletedAssessment) {
      const journeyCard: JourneyCardData = { type: 'journey', id: 'card-journey' };
      result = [...result, journeyCard];
    }
    if (isIntroVisible) {
      const introCard: IntroCardData = { type: 'intro', id: 'card-swipe-intro' };
      result = [introCard, ...result];
    }
    return result;
  }, [cards, isIntroVisible, hasCompletedAssessment]);

  // Map to existing state variables used by CardDeckSection
  const isPlaceholderData = isLoadingRecs;
  const isError = !!recsError;
  const error = recsError ? new Error(recsError) : null;
  const refetch = refetchRecs;

  const {
    hideCloud,
    //   hideFilterBar,
    hideHeaderAvatar
  } = useContentManager()

  return (
    <>
      <HomePageLayout
        timeOfDayConfig={config}
        isTransitioning={isTransitioning}
        showAvatar={!hideHeaderAvatar}
        showCloudLogo={hideHeaderAvatar}
        onCloudClick={startTransition}
      >
        {/* Flex column fills viewport minus header (61px) and footer (72px/88px).
            Card deck grows to fill space; filter bar is always last in the column.
            On small devices the whole column scrolls, keeping filter bar below cards. */}
        <div className="flex flex-col flex-1">
          <motion.div
            className="flex flex-col flex-1"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Cloud Mascot - time-of-day aware */}
            {
              !hideCloud ?
                <motion.div
                  className={`flex flex-col items-center`}
                  variants={itemVariants}
                  style={{
                    opacity: isTransitioning ? 0 : undefined,
                    transition: 'opacity 0s',
                  }}
                >
                  <div
                    ref={cloudRef}
                    onClick={startTransition}
                    className="cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        startTransition();
                      }
                    }}
                    aria-label="Check in together"
                  >
                    <TimeOfDayMascot period={period} size={128} className="sm:hidden" />
                    <TimeOfDayMascot period={period} size={160} className="hidden sm:block" />
                  </div>
                  <div
                    style={{
                      opacity: isTransitioning ? 0 : 1,
                      transition: 'opacity 0.25s ease',
                      transitionDelay: isTransitioning ? '0.15s' : '0s',
                    }}
                  >
                    <div
                      onClick={startTransition}
                      id="title-with-arrow"
                      className="flex flex-row items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          startTransition();
                        }
                      }}
                    >
                      <span
                        className="font-black uppercase text-[10px] md:text-xs lg:text-sm mt-1"
                        style={{
                          color: HOME_V2_COLORS.filterText,
                          letterSpacing: '0.4px',
                          fontFamily: 'DM Sans, sans-serif',
                          fontVariationSettings: "'opsz' 14"
                        }}
                      >CHECK-IN TOGETHER: HOW ARE YOU FEELING?</span>
                      <ArrowRightIcon className="mt-1" size={12} color={HOME_V2_COLORS.filterText} />
                    </div>
                  </div>
                </motion.div>
                : <div className="mt-4 md:mt-6"></div>
            }

            {/* Regeneration indicator */}
            {/* {isRegenerating && (
              <motion.div
                variants={itemVariants}
                className="text-center py-2"
              >
                <span
                  className="text-sm"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 500,
                    color: '#007CAD',
                  }}
                >
                  Finding fresh activities for {firstChild?.nickname ?? 'your child'}...
                </span>
              </motion.div>
            )} */}

            {/* Card Deck Section */}
            <motion.div
              className="flex flex-col items-center mt-12 md:mt-16"
              variants={itemVariants}
              style={{
                opacity: isTransitioning ? 0 : undefined,
                transition: 'opacity 0.25s ease',
                transitionDelay: isTransitioning ? '0.05s' : '0s',
              }}
            >
              <CardDeckSection
                cards={cardsWithAll}
                isPlaceholderData={isPlaceholderData}
                isError={isError}
                error={error}
                refetch={refetch}
                onIntroSwiped={dismissIntro}
                showSwipeInstruction={!isIntroVisible}
              />
            </motion.div>


          </motion.div>

          {/* Filter bar — last in the flex column, sits just above the footer */}
          {
            SHOW_FILTER ?
              <div className="flex justify-center mt-6">
                <HomeV2FiltersContainer onClick={openDialog} />
              </div> : null
          }
        </div>
      </HomePageLayout>

      {/* Check-in transition overlay */}
      {isTransitioning && (
        <CheckInTransitionOverlay
          cloudRect={cloudRect}
          onComplete={onTransitionComplete}
          onClose={resetTransition}
        // onReplay={resetTransition}
        />
      )}

      {/* Filter Dialog - positioned outside layout for full width */}
      {isDialogOpen && (
        <FilterDialog
          isOpen={isDialogOpen}
          onClose={closeDialog}
        />
      )}

      {/* Dev mode period switcher */}
      {/* <DevPeriodSwitcher
        currentPeriod={period}
        overridePeriod={overridePeriod}
        onOverrideChange={setOverridePeriod}
      /> */}
    </>
  );
}


// Render card deck section based on state
const CardDeckSection = (props: {
  cards: DeckCardData[] | undefined;
  isPlaceholderData: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  onIntroSwiped: () => void;
  showSwipeInstruction: boolean;
}) => {
  const { cards, isPlaceholderData, isError, error, refetch, onIntroSwiped } = props;

  const handleSwipe = useCallback(
    (cardId: string, _direction: 'left' | 'right') => {
      if (cardId === 'card-swipe-intro') {
        onIntroSwiped();
      }
    },
    [onIntroSwiped]
  );

  // Error state — show error message with retry
  if (isError) {
    return (
      <ErrorMessage
        message={error?.message ?? 'Failed to load activities'}
        onRetry={refetch}
      />
    );
  }

  // No data yet (shouldn't happen with placeholderData, but defensive)
  if (!cards) {
    return null;
  }

  // After real data arrives with <3 activity cards, show NoActivitiesCard
  // Filter out the intro card so it doesn't count toward the minimum
  if (!isPlaceholderData && cards.filter(c => c.type !== 'intro' && c.type !== 'journey').length < 3) {
    return <NoActivitiesCard />;
  }

  // Loading state (placeholder cards) or success state (real cards)
  // swipeEnabled is false during loading so the skeleton deck can't be dragged
  return (
    <>
      <CardDeck cards={cards} swipeEnabled={!isPlaceholderData} onSwipe={handleSwipe} />
      {/* {showSwipeInstruction && <SwipeInstruction />} */}
    </>
  );
};
